import React, {forwardRef, useEffect, useState} from "react";
import {CalendarApi} from "@fullcalendar/common";
import {KeyValue} from "../../../stores/observables/keyValue";
import {Col, Row} from "reactstrap";
import Flex from "../../atoms/common/flex";
import DropdownBox from "../../molecules/dropdownBox/dropdownBox";
import DatePicker from "react-datepicker";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {useTranslation} from "react-i18next";
import HeaderProgressBar from "../../atoms/headerProgressBar/headerProgressBar";
import EventPrintableCalendarModal from "../eventPrintableCalendarModal/eventPrintableCalendarModal";
import "./eventCalendarHeader.sass";
import {IconSource} from "../../../shared/enums/iconSource";
import {ObservableList} from "../../../stores/observables/observableList";
import Tag from "../../atoms/tag/tag";
import {
  getMonthKeyValuePairs,
  isDateOneAfterDateTwo,
  isDateOneBetweenTwoDates,
  isDateOneSameOrAfterDateTwo,
  isDateOneSameOrBeforeDateTwo
} from "../../../utils/dateUtils";
import CalendarSchedule from "../../../stores/domainStore/pages/models/calendarSchedule";
import {addDays, format} from "date-fns";
import {observer} from "mobx-react-lite";
import NavigationButton from "../../atoms/button/navigationButton";
import Banner from "../../atoms/banner/banner";
import {
  calendarViewCategory,
  calendarViewMonth,
  calendarViewSummary,
  calendarViewWeek
} from "../../../shared/constants";

export interface IEventCalendarHeaderProps {
  calendarApi: CalendarApi;
  updateCalendarDate: () => void;
  currentCalendarView: string;
  handleViewChange: (selectedView: string) => void;
  isLoading: boolean;
  calendarSchedule?: CalendarSchedule;
  isMultiVillageOptionSelected?: boolean;
}

const EventCalendarHeader: React.FC<IEventCalendarHeaderProps> = ({
  calendarApi,
  updateCalendarDate,
  currentCalendarView,
  handleViewChange,
  isLoading,
  calendarSchedule,
  isMultiVillageOptionSelected
}) => {
  const [
    isEventPrintableCalendarModalOpen,
    setEventPrintableCalendarModalOpen
  ] = useState(false);
  const [bannerMessage, setBannerMessage] = useState<string>("");
  const {t} = useTranslation(["translation", "event"]);
  const views = [
    new KeyValue(calendarViewSummary, t("event:labels.calendar.summaryView")),
    new KeyValue(calendarViewWeek, t("event:labels.calendar.weekView")),
    new KeyValue(calendarViewMonth, t("event:labels.calendar.monthView"))
  ];
  if (!isMultiVillageOptionSelected) {
    views.unshift(
      new KeyValue(
        calendarViewCategory,
        t("event:labels.calendar.categoryView")
      )
    );
  }
  const filterDropdownItems = new ObservableList<KeyValue>(
    views,
    currentCalendarView
  );

  useEffect(() => {
    let bannerMessage = "";
    const activeStart =
      calendarApi.currentDataManager?.getCurrentData().viewApi.currentStart;
    const activeEnd =
      calendarApi.currentDataManager?.getCurrentData().viewApi.currentEnd;
    if (
      activeStart &&
      activeEnd &&
      isDateOneAfterDateTwo(calendarSchedule?.endDate, activeStart) &&
      isDateOneAfterDateTwo(activeEnd, calendarSchedule?.startDate)
    ) {
      bannerMessage = t("event:messages.calendarRelease", {
        monthName: format(calendarSchedule?.startDate!, "MMMM"),
        releaseDate: format(
          calendarSchedule?.releaseDate!,
          "do' of 'MMMM' at 'h:mmaaa"
        )
      });
    }
    setBannerMessage(bannerMessage);
  }, [calendarApi, calendarSchedule, t]);

  // @ts-ignore
  const selectDateButton = forwardRef(({value, onClick}, ref) => (
    <div className="mr-2">
      <ButtonIcon type="secondary" icon={IconSource.calendar} onClick={onClick}>
        {t("event:labels.calendar.selectDate")}
      </ButtonIcon>
    </div>
  ));

  const exportCalendarDates: ObservableList<KeyValue> = getMonthKeyValuePairs();

  const UnreleasedPill = () => {
    if (isMultiVillageOptionSelected) {
      return null;
    }

    const activeStart =
      calendarApi.currentDataManager?.getCurrentData().viewApi.activeStart ||
      "";
    let activeEnd =
      calendarApi.currentDataManager?.getCurrentData().viewApi.activeEnd;
    const currentEnd =
      calendarApi.currentDataManager?.getCurrentData().viewApi.currentEnd;
    // end date is giving one extra day.So, removing the extra day.
    if (activeEnd) activeEnd = addDays(activeEnd, -1);
    if (activeStart && activeEnd && calendarSchedule?.startDate) {
      let isUnreleasedPill =
        isDateOneBetweenTwoDates(
          calendarSchedule.startDate,
          activeStart,
          activeEnd
        ) ||
        isDateOneSameOrAfterDateTwo(activeStart, calendarSchedule.startDate);
      if (
        filterDropdownItems?.selectedItem?.value === calendarViewMonth &&
        isDateOneSameOrBeforeDateTwo(currentEnd, calendarSchedule.startDate)
      ) {
        isUnreleasedPill = false;
      }
      return isUnreleasedPill ? (
        <Tag text={t("event:labels.calendar.unreleased")} type="neutral" />
      ) : (
        <Tag text={t("event:labels.calendar.released")} type="success" />
      );
    } else return null;
  };

  return (
    <>
      <EventPrintableCalendarModal
        isOpen={isEventPrintableCalendarModalOpen}
        toggle={() =>
          setEventPrintableCalendarModalOpen(!isEventPrintableCalendarModalOpen)
        }
        exportCalendarDates={exportCalendarDates}
      />
      <Row noGutters className="align-items-center calendar-options">
        <Col xs md="auto" className="order-md-1 mr-2">
          <ButtonIcon
            type="secondary"
            onClick={() => {
              calendarApi.today();
              updateCalendarDate();
            }}
          >
            {t("translation:time.today")}
          </ButtonIcon>
        </Col>
        <Col xs md="auto" tag={Flex} justify="end" className="order-md-2">
          <DatePicker
            calendarStartDay={1}
            selected={
              calendarApi.currentDataManager?.getCurrentData().viewApi
                .currentStart
            }
            onChange={(value) => {
              calendarApi.gotoDate(value);
              updateCalendarDate();
            }}
            dateFormat="dd/MM/yyyy"
            customInput={React.createElement(selectDateButton)}
          />
        </Col>
        <Col xs="auto" className="d-flex justify-content-end order-md-3">
          <div className="mr-1">
            <NavigationButton
              icon={IconSource.leftArrow}
              tooltip={t("event:labels.calendar.previous")}
              onClick={() => {
                calendarApi.prev();
                updateCalendarDate();
              }}
            />
          </div>
          <div className="mr-1">
            <NavigationButton
              icon={IconSource.rightArrow}
              tooltip={t("event:labels.calendar.next")}
              onClick={() => {
                calendarApi.next();
                updateCalendarDate();
              }}
            />
          </div>
        </Col>
        <Col xs="auto" md="auto" className="order-md-4">
          <h4 className="mb-0 fs-0 calendar-title">
            {`${calendarApi.currentDataManager?.getCurrentData().viewTitle}`}
          </h4>
        </Col>
        <Col xs="auto" md="auto" className="order-md-5 ml-3 d-flex">
          {UnreleasedPill()}
        </Col>
        <Col md="auto" className="d-md-none">
          <hr />
        </Col>
        <Col className="d-flex justify-content-end order-md-5 mr-2">
          <DropdownBox
            options={filterDropdownItems}
            onItemSelected={(selectedItem) =>
              handleViewChange(selectedItem.value)
            }
          />
        </Col>
        {!isMultiVillageOptionSelected && (
          <Col xs md="auto" className="d-flex order-md-6">
            <ButtonIcon
              type="secondary"
              icon={IconSource.export}
              onClick={() => setEventPrintableCalendarModalOpen(true)}
            >
              {t("translation:actions.export")}
            </ButtonIcon>
          </Col>
        )}
      </Row>
      <div className="calender-header">
        {bannerMessage && !isMultiVillageOptionSelected && (
          <Banner text={bannerMessage} icon={IconSource.notification} />
        )}
      </div>
      <HeaderProgressBar progressBarLoading={isLoading} />
    </>
  );
};

export default observer(EventCalendarHeader);
